import { updateElements } from "../utils/updateElements";
import { debounce } from "../utils/debounce";
import { translate, ERR } from "../utils/i18n";
import { formatMoney, parseMoneyTextToCents } from "../utils/money";

interface SelectedBundleVariant {
  id: number;
  quantity: number;
}

interface CartItem {
  id: number;
  quantity: number;
}

interface DiscountRule {
  title: string;
  minimum_quantity_of_items: number;
  percentage: number;
  is_active?: boolean;
}

interface PriceState {
  priceRegular: number;
  priceSale: number;
  priceSave: number;
  discountPercentage: number;
}

interface CachedElements {
  productInfo: HTMLElement | null;
  quantityInput: HTMLInputElement | null;
  errorMessageContainer: HTMLElement | null;
  productCountEl: HTMLElement | null;
  cartItemsEl: HTMLElement | null;
  priceSaleEl: HTMLElement | null;
  priceBadgeEl: HTMLElement | null;
  priceRegularEl: HTMLElement | null;
  addBundleButton: HTMLButtonElement | null;
  bundleGroups: NodeListOf<HTMLElement> | null;
  bundleInputs: NodeListOf<HTMLInputElement> | null;
}

type FixedBasePrices = { sale: number; compare: number | null };
const fixedBasePriceCache = new WeakMap<Element, FixedBasePrices>();

function getFixedPriceContainer(): Element | null {
  const info = document.querySelector<HTMLElement>(selectors.productInfo);
  return info?.querySelector(".product-info__price") || info || null;
}

function getFixedBasePrices(): FixedBasePrices | null {
  const container = getFixedPriceContainer();
  if (!container) return null;
  const cached = fixedBasePriceCache.get(container);
  if (cached) return cached;
  const sale = centsFromText(elements.priceSaleEl);
  if (sale === null) return null;
  const compare = centsFromText(elements.priceRegularEl);
  const base = { sale, compare };
  fixedBasePriceCache.set(container, base);
  return base;
}

const ERROR_ICON_SVG = `
<svg role="presentation" focusable="false" width="18" height="18" class="offset-icon icon icon-error" style="--icon-height: 18px" viewBox="0 0 18 18">
<path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="currentColor"></path>
<path d="M5.29289 6.70711L11.2929 12.7071L12.7071 11.2929L6.70711 5.29289L5.29289 6.70711ZM6.70711 12.7071L12.7071 6.70711L11.2929 5.2929L5.29289 11.2929L6.70711 12.7071Z" fill="#ffffff"></path>
</svg>
`;

const ERROR_MESSAGES = ERR;

const selectors = {
  productInfo: "[data-product-info]",
  quantitySelector: "[data-quantity-selector]",
  errorMessageContainer: "[data-bundle-error-message]",
  productCount: "[data-product-count]",
  priceSale: "[data-price-sale]",
  priceBadge: "[data-price-badge]",
  priceRegular: "[data-price-regular]",
  productData: "[data-product-data]",
  dataCartItems: "[data-cart-items]",
  bundleGroup: "[data-bundle-group]",
  dataVariantRadioInput: "[data-variant-radio-input]",
  addBundleButton: "[data-add-bundle-button]",
};

let elements: CachedElements = {
  productInfo: null,
  quantityInput: null,
  errorMessageContainer: null,
  productCountEl: null,
  cartItemsEl: null,
  priceSaleEl: null,
  priceBadgeEl: null,
  priceRegularEl: null,
  addBundleButton: null,
  bundleGroups: null,
  bundleInputs: null,
};

function ensureAddButtonListener(): void {
  const btn = elements.addBundleButton;
  if (!btn) return;
  btn.removeEventListener("click", handleAddToCart);
  btn.addEventListener("click", handleAddToCart);
}

function cacheElements(): void {
  const productInfo = document.querySelector<HTMLElement>(selectors.productInfo);

  elements = {
    productInfo,
    quantityInput:
      productInfo?.querySelector<HTMLInputElement>(selectors.quantitySelector) ??
      document.querySelector<HTMLInputElement>(selectors.quantitySelector),
    errorMessageContainer: document.querySelector<HTMLElement>(
      selectors.errorMessageContainer,
    ),
    productCountEl: document.querySelector<HTMLElement>(selectors.productCount),
    cartItemsEl: document.querySelector<HTMLElement>(selectors.dataCartItems),
    priceSaleEl:
      productInfo?.querySelector<HTMLElement>(selectors.priceSale) ||
      productInfo?.querySelector<HTMLElement>("sale-price") ||
      productInfo?.querySelector<HTMLElement>(".text-on-sale") ||
      null,
    priceBadgeEl:
      productInfo?.querySelector<HTMLElement>(selectors.priceBadge) ||
      productInfo?.querySelector<HTMLElement>("on-sale-badge, .badge--on-sale") ||
      null,
    priceRegularEl:
      productInfo?.querySelector<HTMLElement>(selectors.priceRegular) ||
      productInfo?.querySelector<HTMLElement>("compare-at-price, .line-through") ||
      null,
    addBundleButton: document.querySelector<HTMLButtonElement>(
      selectors.addBundleButton,
    ),
    bundleGroups: document.querySelectorAll<HTMLElement>(selectors.bundleGroup),
    bundleInputs: document.querySelectorAll<HTMLInputElement>(
      selectors.dataVariantRadioInput,
    ),
  };
}

// currency code is resolved by Liquid money formats; no need to store here

function centsFromText(el: Element | null): number | null {
  return parseMoneyTextToCents(el?.textContent || "");
}

function centsFromDataset(
  el: HTMLElement | null,
  key: string,
): number | null {
  if (!el) return null;
  const raw = el.dataset[key];
  if (!raw) return null;
  const parsed = Number.parseInt(raw, 10);
  if (Number.isNaN(parsed)) return null;
  return parsed;
}

function readCents(
  el: HTMLElement | null,
  datasetKey: string,
): number | null {
  return centsFromDataset(el, datasetKey) ?? centsFromText(el);
}

function parseNonNegativeInt(value: string | null): number | null {
  if (typeof value !== "string" || value.trim() === "") return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function getProductCount(): number {
  return (
    parseInt(elements.productCountEl?.dataset.productCount ?? "0", 10) || 0
  );
}

function getCartItemCount(): number {
  return parseInt(elements.cartItemsEl?.dataset.cartItems ?? "0", 10) || 0;
}

function getProductQuantity(): number {
  return parseInt(elements.quantityInput?.value ?? "0", 10) || 0;
}

function isFixBundleContext(): boolean {
  return !!document.querySelector(".hidden-variant-picker");
}

function displayErrorMessage(message: string): void {
  const container = elements.errorMessageContainer;
  if (!container) {
    console.warn("Error message container not found. Using fallback banner.");
    const toast = document.createElement("div");
    toast.setAttribute("role", "status");
    toast.setAttribute("aria-live", "polite");
    toast.className = "toast toast--error";
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3500);
    return;
  }

  container.classList.remove("is-hidden", "is-hiding");
  container.classList.add("is-visible");
  container.innerHTML = `${ERROR_ICON_SVG} <span>${message}</span>`;

  setTimeout(() => container.classList.add("is-hiding"), 3500);
  setTimeout(() => {
    container.classList.remove("is-visible", "is-hiding");
    container.classList.add("is-hidden");
  }, 4000);
}

function getSelectedBundleVariants(
  setQuantity: number,
): SelectedBundleVariant[] | null {
  const selectedVariants: SelectedBundleVariant[] = [];
  if (!elements.bundleGroups || elements.bundleGroups.length === 0) {
    console.warn("No bundle groups found to collect items from.");
    return null;
  }

  for (const bundleGroup of elements.bundleGroups) {
    const selectedVariant = bundleGroup.querySelector<HTMLInputElement>(
      'input[type="radio"]:checked',
    );

    if (!selectedVariant) {
      displayErrorMessage(ERROR_MESSAGES.INVALID_SELECTION);
      return null;
    }

    const variantId = parseInt(selectedVariant.value, 10);
    if (isNaN(variantId)) {
      displayErrorMessage(ERROR_MESSAGES.INVALID_VARIANT);
      return null;
    }

    const qtyEachRaw = selectedVariant.dataset.variantQty;
    const qtyEach = qtyEachRaw ? parseInt(qtyEachRaw, 10) || 1 : 1;
    const totalQty = Math.max(1, setQuantity) * Math.max(1, qtyEach);
    selectedVariants.push({ id: variantId, quantity: totalQty });
  }

  return selectedVariants;
}

async function getErrorMessageFromResponse(
  response: Response,
): Promise<string> {
  try {
    const errorData = await response.json();
    return (
      errorData?.description || errorData?.message || ERROR_MESSAGES.DEFAULT
    );
  } catch {
    switch (response.status) {
      case 400:
        return ERR.INVALID_REQUEST;
      case 404:
        return ERR.NOT_FOUND;
      case 422:
        return ERR.UNPROCESSABLE;
      case 500:
        return ERR.SERVER_ERROR;
      default:
        return ERR.UNEXPECTED;
    }
  }
}

let cachedDiscountRules: DiscountRule[] | null = null;
let lastDiscountRulesSource: string | null = null;

function hasType(
  source: object,
  key: PropertyKey,
  expectedType: "number" | "boolean",
): boolean {
  const propValue: unknown = Reflect.get(source, key);
  return typeof propValue === expectedType;
}

function isUndefined(source: object, key: PropertyKey): boolean {
  const propValue: unknown = Reflect.get(source, key);
  return typeof propValue === "undefined";
}

function isDiscountRule(value: unknown): value is DiscountRule {
  if (!value || typeof value !== "object") return false;
  return (
    hasType(value, "percentage", "number") &&
    hasType(value, "minimum_quantity_of_items", "number") &&
    (hasType(value, "is_active", "boolean") || isUndefined(value, "is_active"))
  );
}

function invalidateDiscountRulesCache() {
  cachedDiscountRules = null;
  lastDiscountRulesSource = null;
}

function getDiscountRules(): DiscountRule[] {
  try {
    const json = elements.productInfo?.dataset.discountRules ?? "[]";
    if (json !== lastDiscountRulesSource || !cachedDiscountRules) {
      const parsedUnknown: unknown = JSON.parse(json);
      const arr = Array.isArray(parsedUnknown) ? parsedUnknown : [];
      const rules = arr.filter(isDiscountRule);
      cachedDiscountRules = rules.filter(
        (rule) =>
          rule.minimum_quantity_of_items >= 0 &&
          rule.percentage >= 0 &&
          rule.is_active !== false,
      );
      lastDiscountRulesSource = json;
    }
    return cachedDiscountRules ?? [];
  } catch (err) {
    console.warn("Failed to parse or resolve discount rules; using empty set.", err);
    return [];
  }
}

function getBestDiscount(totalQuantity: number, rules: DiscountRule[]): number {
  return rules.reduce((maxDiscount, rule) => {
    if (totalQuantity >= rule.minimum_quantity_of_items) {
      return Math.max(maxDiscount, rule.percentage);
    }
    return maxDiscount;
  }, 0);
}

function buildPriceState(
  regularTotalCents: number,
  saleTotalCents: number,
  percentageOverride?: number,
): PriceState {
  const regularCents = Math.max(0, regularTotalCents);
  const saleCents = Math.max(0, Math.min(regularCents, saleTotalCents));
  const saveCents = Math.max(0, regularCents - saleCents);
  const computedPercentage =
    regularCents > 0 ? Math.round((saveCents / regularCents) * 100) : 0;
  const discountPercentage =
    typeof percentageOverride === "number" ? percentageOverride : computedPercentage;

  return {
    priceRegular: regularCents / 100,
    priceSale: saleCents / 100,
    priceSave: saveCents / 100,
    discountPercentage,
  };
}

function getPriceState(): PriceState | null {
  if (isFixBundleContext()) {
    const quantity = getProductQuantity();
    if (quantity < 1) return null;

    const base = getFixedBasePrices();
    if (!base) return null;
    const unitSaleCents = base.sale;
    const unitCompareCents = base.compare ?? unitSaleCents;
    const qty = Math.max(quantity, 1);
    return buildPriceState(unitCompareCents * qty, unitSaleCents * qty);
  }

  const quantity = getProductQuantity();
  if (quantity < 1) return null;

  const productCount = getProductCount();
  const cartCount = getCartItemCount();
  const rules = getDiscountRules();
  const totalQuantity = productCount * quantity + cartCount;
  const discountPercentage = getBestDiscount(totalQuantity, rules);

  const bundleInputs = elements.bundleInputs;
  let state: PriceState | null = null;
  if (!bundleInputs || bundleInputs.length === 0) {
    state = getSimpleProductPriceState(quantity, discountPercentage);
  } else {
    state = getBundleProductPriceState(
      bundleInputs,
      quantity,
      discountPercentage,
    );
  }

  if (!state) return null;
  return withPlaceholderTotals(state, quantity, discountPercentage);
}

function getSimpleProductPriceState(
  quantity: number,
  discountPercentage: number,
): PriceState | null {
  const regularCents = readCents(elements.priceRegularEl, "priceRegular");
  if (regularCents === null) return null;
  const saleCents = readCents(elements.priceSaleEl, "priceSale");
  const qty = Math.max(quantity, 1);
  const regularTotal = regularCents * qty;
  const discountCents = Math.round((regularTotal * discountPercentage) / 100);
  let saleTotal = Math.max(0, regularTotal - discountCents);

  if (saleCents !== null) {
    const manualTotal = saleCents * qty;
    saleTotal = discountPercentage > 0 ? Math.min(saleTotal, manualTotal) : manualTotal;
  }

  return buildPriceState(regularTotal, saleTotal, discountPercentage);
}

function getBundleProductPriceState(
  inputs: NodeListOf<HTMLInputElement>,
  quantity: number,
  discountPercentage: number,
): PriceState | null {
  let bundleBaseCents = 0;
  let bundleDiscountedCents = 0;

  inputs.forEach((input) => {
    if (!input.checked) return;
    const variantPriceCents = Number.parseInt(input.dataset.variantPrice ?? "0", 10);
    if (Number.isNaN(variantPriceCents)) return;
    const variantQuantityInSet = input.dataset.variantQty
      ? Number.parseInt(input.dataset.variantQty, 10) || 1
      : 1;
    const variantSubtotalCents = variantPriceCents * variantQuantityInSet;
    bundleBaseCents += variantSubtotalCents;
    const variantDiscountCents = Math.round(
      (variantSubtotalCents * discountPercentage) / 100,
    );
    bundleDiscountedCents += variantSubtotalCents - variantDiscountCents;
  });

  if (bundleBaseCents <= 0) return null;
  const qty = Math.max(quantity, 1);
  const regularTotal = bundleBaseCents * qty;
  const saleTotal = bundleDiscountedCents * qty;
  return buildPriceState(regularTotal, saleTotal, discountPercentage);
}

function getPlaceholderContributionCents(
  discountPercentage: number,
): { baseCents: number; saleCents: number } {
  const root: ParentNode = elements.productInfo || document;
  const placeholders = root.querySelectorAll<HTMLElement>(
    "[data-bundle-group-by-tag]",
  );
  if (placeholders.length === 0) return { baseCents: 0, saleCents: 0 };

  let baseCents = 0;
  let saleCents = 0;

  placeholders.forEach((el) => {
    const price = parseNonNegativeInt(el instanceof HTMLElement ? el.dataset.currentVariantPrice ?? null : null);
    const qtyEach = parseNonNegativeInt(el instanceof HTMLElement ? el.dataset.quantity ?? null : null) || 1;
    if (price === null) return;
    const subtotal = price * Math.max(1, qtyEach);
    baseCents += subtotal;
    const discount = Math.round((subtotal * discountPercentage) / 100);
    saleCents += subtotal - discount;
  });

  return { baseCents, saleCents };
}

function withPlaceholderTotals(
  state: PriceState,
  quantity: number,
  discountPercentage: number,
): PriceState {
  const { baseCents, saleCents } = getPlaceholderContributionCents(
    discountPercentage,
  );
  if (baseCents <= 0) return state;

  const qty = Math.max(quantity, 1);
  const regularTotal = Math.round(state.priceRegular * 100) + baseCents * qty;
  const saleTotal = Math.round(state.priceSale * 100) + saleCents * qty;
  return buildPriceState(regularTotal, saleTotal, discountPercentage);
}

function updatePriceUI(): void {
  const state = getPriceState();
  if (!state) return;

  const { priceSale, priceRegular, priceSave, discountPercentage } = state;

  const format = (val: number) => formatMoney(val);

  const saleElement = elements.priceSaleEl;
  const regularElement = elements.priceRegularEl;
  const hasReduction = priceRegular - priceSale > 0.001;
  const showSeparateSale =
    hasReduction && saleElement !== null && saleElement !== regularElement;

  if (regularElement) {
    regularElement.textContent = format(priceRegular);
    regularElement.setAttribute(
      "aria-label",
      `${translate("product.price.regular", "Regular price")}: ${format(priceRegular)}`,
    );
    regularElement.style.textDecoration = showSeparateSale ? "line-through" : "";
    const shouldHideRegular =
      !showSeparateSale && !!saleElement && saleElement !== regularElement;
    regularElement.style.display = shouldHideRegular ? "none" : "";
  }

  if (saleElement) {
    const saleClassList = saleElement.classList;
    const ensureTextSize = () => {
      if (!saleClassList.contains("text-lg")) {
        saleClassList.add("text-lg");
      }
    };

    if (saleElement === regularElement || !regularElement) {
      const labelKey = hasReduction
        ? "product.price.current"
        : "product.price.regular";
      const labelFallback = hasReduction ? "Price" : "Regular price";
      const value = hasReduction ? priceSale : priceRegular;
      saleElement.textContent = format(value);
      saleElement.setAttribute(
        "aria-label",
        `${translate(labelKey, labelFallback)}: ${format(value)}`,
      );
      saleElement.style.textDecoration = "";
      saleElement.style.display = "";
      saleClassList.toggle("text-on-sale", hasReduction);
      ensureTextSize();
    } else {
      saleElement.textContent = format(priceSale);
      saleElement.setAttribute(
        "aria-label",
        `${translate("product.price.current", "Price")}: ${format(priceSale)}`,
      );
      saleElement.style.display = "";
      saleClassList.toggle("text-on-sale", hasReduction);
      ensureTextSize();
    }
  }

  if (elements.priceBadgeEl) {
    if (priceSave > 0) {
      elements.priceBadgeEl.textContent = `${translate("product.price.save", "Save")} ${format(priceSave)}`;
      elements.priceBadgeEl.setAttribute(
        "aria-label",
        `${translate("product.price.save", "Save")}: ${format(priceSave)} (${discountPercentage}%)`,
      );
      elements.priceBadgeEl.style.display = "";
    } else {
      elements.priceBadgeEl.style.display = "none";
    }
  }
}

type PostToCartResult =
  | { success: true; data: unknown }
  | { success: false; error: string };

async function postToCart(
  items: CartItem[],
  addButton: HTMLButtonElement,
): Promise<PostToCartResult> {
  if (!addButton) return { success: false, error: ERROR_MESSAGES.DEFAULT };

  addButton.setAttribute("aria-busy", "true");
  addButton.disabled = true;
  const originalButtonText = addButton.textContent;
  addButton.textContent = translate("cart.adding", "Adding...");

  try {
    if (window.CartJS && typeof window.CartJS.addItems === "function") {
      const cartJsItems = items.map((it) => ({ id: String(it.id), quantity: it.quantity }));
      await window.CartJS.addItems(cartJsItems);
      return { success: true, data: { via: "CartJS" } };
    }

    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ items }),
    });

    if (!response.ok) {
      const errorMessage = await getErrorMessageFromResponse(response);
      displayErrorMessage(errorMessage);
      return { success: false, error: errorMessage };
    }

    const responseData: unknown = await response.json();
    return { success: true, data: responseData };
  } catch (error) {
    const message = error instanceof Error ? error.message : ERROR_MESSAGES.DEFAULT;
    displayErrorMessage(message);
    return { success: false, error: message };
  } finally {
    addButton.removeAttribute("aria-busy");
    addButton.disabled = false;
    addButton.textContent = originalButtonText;
  }
}

async function handleAddToCart(): Promise<void> {
  if (!elements.addBundleButton) return;

  const quantity = getProductQuantity();
  if (quantity < 1) {
    displayErrorMessage(ERROR_MESSAGES.INVALID_QUANTITY);
    return;
  }

  const isBundle = elements.bundleGroups && elements.bundleGroups.length > 0;
  let selectedVariants: SelectedBundleVariant[] | null = null;

  if (isBundle) {
    selectedVariants = getSelectedBundleVariants(quantity);
    if (selectedVariants === null) return;
  } else {
    const variantInput = document.querySelector<HTMLInputElement>(
      'input[name="id"]:checked, input[name="id"]',
    );
    const variantId = variantInput ? parseInt(variantInput.value, 10) : null;
    if (!variantId || isNaN(variantId)) {
      displayErrorMessage(ERROR_MESSAGES.INVALID_VARIANT);
      return;
    }
    selectedVariants = [{ id: variantId, quantity }];
  }

  if (!selectedVariants || selectedVariants.length === 0) {
    displayErrorMessage(ERROR_MESSAGES.DEFAULT);
    return;
  }

  const result = await postToCart(selectedVariants, elements.addBundleButton);

  if (result.success) {
    try {
      const delta = selectedVariants.reduce((sum, v) => sum + (v.quantity || 0), 0);
      if (delta > 0) {
        const cartItemsNode = elements.cartItemsEl || document.querySelector<HTMLElement>("[data-cart-items]");
        if (cartItemsNode) {
          const current = parseInt(cartItemsNode.dataset.cartItems ?? "0", 10) || 0;
          cartItemsNode.dataset.cartItems = String(current + delta);
          updatePriceUI();
        }
      }
    } catch (err) {
      console.warn("Optimistic cart count update failed; falling back to server refresh.", err);
    }

    document.dispatchEvent(new CustomEvent("cart:refresh"));

    const cartLink = document.querySelector('a[aria-controls="cart-drawer"]');
    if (cartLink instanceof HTMLElement) {
      cartLink.click();
    }
  }
}

const debouncedPriceUpdate = debounce(() => {
  updatePriceUI();
}, 300);

function ensureQuantityListeners(): void {
  const input = elements.quantityInput;
  if (!input) return;
  input.removeEventListener("change", debouncedPriceUpdate);
  input.removeEventListener("input", debouncedPriceUpdate);
  input.addEventListener("change", debouncedPriceUpdate);
  input.addEventListener("input", debouncedPriceUpdate);
}

async function handleCartUpdate(): Promise<void> {
  const selectorsToUpdate = !isFixBundleContext()
    ? [
        selectors.dataCartItems,
        ".header__cart-count",
        selectors.productData,
        "product-rerender",
        ".product-info__badge-list",
        ".bundle-item__info",
      ]
    : [
        selectors.dataCartItems,
        ".header__cart-count",
        selectors.productData,
        "product-rerender",
        ".product-info__badge-list",
        ".bundle-item__info",
      ];
  await updateElements(selectorsToUpdate);
  cacheElements();
  ensureAddButtonListener();
  debouncedPriceUpdate();
  // Nudge bundle discount UIs (built client-side) to recalc immediately
  document.dispatchEvent(new CustomEvent("bundle:refresh-discount"));
}

async function handleBundleInputChange(event: Event) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (!target.matches("[data-variant-radio-input]")) return;

  document.dispatchEvent(new CustomEvent("variant:change"));
}

function bindEventListeners(): void {
  ensureAddButtonListener();

  if (elements.quantityInput) {
    ensureQuantityListeners();
  }

  const debouncedCartUpdate = debounce(handleCartUpdate, 120);
  document.addEventListener("cart:change", debouncedCartUpdate);
  document.addEventListener("cart:refresh", debouncedCartUpdate);

  document.addEventListener("cart:change", invalidateDiscountRulesCache);
  document.addEventListener("cart:refresh", invalidateDiscountRulesCache);
  document.addEventListener("variant:change", invalidateDiscountRulesCache);
  document.addEventListener(
    "shopify:section:load",
    invalidateDiscountRulesCache,
  );
  const debouncedRefreshUI = debounce(() => {
    cacheElements();
    ensureQuantityListeners();
    ensureAddButtonListener();
    debouncedPriceUpdate();
  }, 150);
  document.addEventListener("shopify:section:load", debouncedRefreshUI);

  // Delegate change events for dynamically inserted bundle inputs
  document.addEventListener("change", (event) => {
    const t = event.target;
    if (!(t instanceof HTMLInputElement)) return;
    if (!t.matches("[data-variant-radio-input]")) return;
    handleBundleInputChange(event);
  });

  document.addEventListener("variant:change", debouncedRefreshUI);

}

function main() {
  cacheElements();
  bindEventListeners();
  updatePriceUI();
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", main);
} else {
  main();
}
